import CommunityQuickSetup from './CommunityQuickSetup';
import UserManagementPanel from './UserManagementPanel';
import StoreAdminPanel from './StoreAdminPanel';
import ContactsDirectory from './ContactsDirectory';
import FinancialHub from '../finance/FinancialHub';
import { ErueNewsPortal } from '../festival/ErueNewsPortal';
import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, Settings, Users, Globe, FileText,
  Bell, Image, BarChart3, Calendar, Shield, LogOut,
  ChevronRight, Edit3, Eye, EyeOff, CheckCircle,
  AlertTriangle, Loader, Menu, X, Database,
  Newspaper, Map, DollarSign, UserCheck, Download, Store
} from 'lucide-react';

interface Props {
  onLogout: () => void;
  onNavigate: (tab: string) => void;
  currentUser: any;
}

interface DashStat {
  label: string;
  value: string | number;
  icon: string;
  color: string;
}

interface PortalStatus {
  id: string;
  name: string;
  published: boolean;
  posts: number;
  team: number;
  hasChief: boolean;
  hasHero: boolean;
}

const NAV_ITEMS = [
  { id: 'dashboard',    label: 'Dashboard',         icon: LayoutDashboard },
  { id: 'hero',         label: 'Hero Images',        icon: Image },
  { id: 'communities',  label: 'Community Portals',  icon: Map },
  { id: 'quicksetup',   label: 'Quick Setup',         icon: Settings },
  { id: 'content',      label: 'News & Publishing',  icon: Newspaper },
  { id: 'users',        label: 'Users & Roles',      icon: Users },
  { id: 'festival',     label: 'Festival Settings',  icon: Calendar },
  { id: 'registrations',label: 'Registrations',      icon: UserCheck },
  { id: 'financial',    label: 'Financial',          icon: DollarSign },
  { id: 'analytics',    label: 'Analytics',          icon: BarChart3 },
  { id: 'financial-hub', label: 'Financial Hub', icon: '💰' },
          { id: 'contacts-directory', label: 'Contacts Directory', icon: '📋' },
          { id: 'store-admin',  label: 'Festival Store',      icon: Store },    
  { id: 'system',       label: 'System Settings',    icon: Settings },
];

const SuperAdminDashboard: React.FC<Props> = ({ onLogout, onNavigate, currentUser }) => {
  const [activeNav, setActiveNav]       = useState('dashboard');
  const [sidebarOpen, setSidebarOpen]   = useState(false);
  const [stats, setStats]               = useState<DashStat[]>([]);
  const [portals, setPortals]           = useState<PortalStatus[]>([]);
  const [loading, setLoading]           = useState(true);
  const [newsStats, setNewsStats]       = useState({ total:0, published:0, drafts:0, breaking:0 });
  const [heroImages, setHeroImages]     = useState({ palaceImageUrl:'', ogieneniImageUrl:'', logoImageUrl:'' });
  const [heroSaving, setHeroSaving]     = useState(false);
  const [heroSaved, setHeroSaved]       = useState(false);
  const [siteSettings, setSiteSettings] = useState({
    siteName:       'ERUE Festival Suite',
    tagline:        '"Uzairue Heritage: Culture, Security, Prosperity"',
    festivalStart:  '2026-12-24',
    festivalEnd:    '2026-12-30',
    grandFinale:    'Wednesday, 30 December 2026',
    venueName:      'Palace of the Ogieneni of Uzairue Kingdom',
    venueAddress:   'Jattu-Uzairue, Etsako West LGA, Edo State, Nigeria',
    contactEmail:   'erueuzairue@gmail.com',
    contactPhone:   '',
    facebookUrl:    '',
    whatsappNumber: '',
  });
  const [settingsSaved, setSettingsSaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // Load hero images
        const hr = await fetch('/api/portal/hero-images');
        const hd = await hr.json();
        if (hd.success) setHeroImages(hd.images);

        // Load site settings
        const sr = await fetch('/api/portal/site-settings');
        const sd = await sr.json();
        if (sd.success && sd.settings) setSiteSettings(s => ({ ...s, ...sd.settings }));

        // Load portals summary
        const pr = await fetch('/api/community-portals');
        const pd = await pr.json();
        if (pd.success) {
          const portalList: PortalStatus[] = (pd.portals || []).map((p: any) => ({
            id:        p.villageId,
            name:      p.villageName || p.villageId,
            published: p.published || false,
            posts:     (p.posts || []).length,
            team:      (p.team || []).length,
            hasChief:  !!p.chiefName,
            hasHero:   !!p.heroImageUrl,
          }));
          setPortals(portalList);
        }

        // Build stats
        setStats([
          { label: 'Days to Festival',  value: Math.max(0, Math.ceil((new Date('2026-12-30').getTime() - Date.now()) / 86400000)), icon: '📅', color: 'text-[#C9A84C]' },
          { label: 'Communities',       value: 22,                                           icon: '🏘️', color: 'text-[#0F5132]' },
          { label: 'Published Portals', value: (pd.portals || []).filter((p: any) => p.published).length, icon: '🌐', color: 'text-green-400' },
          { label: 'Festival Date',     value: '30 Dec 2026',                               icon: '🎉', color: 'text-[#C9A84C]' },
        ]);
      } catch (e) {
        console.error('Dashboard load error', e);
      } finally {
        // Fetch news stats
      try {
        const nr = await fetch('/api/admin/news', { headers: { Authorization: `Bearer ${localStorage.getItem('erue_auth_token')||''}` } });
        const nd = await nr.json();
        if (nd.success) {
          const arts = nd.articles || [];
          setNewsStats({ total:arts.length, published:arts.filter((a:any)=>a.status==='published').length, drafts:arts.filter((a:any)=>a.status==='draft').length, breaking:arts.filter((a:any)=>a.isBreaking&&a.status==='published').length });
        }
      } catch {}
      setLoading(false);
      }
    };
    load();
  }, []);

  const saveHeroImages = async () => {
    setHeroSaving(true);
    try {
      const r = await fetch('/api/portal/hero-images', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(heroImages),
      });
      const d = await r.json();
      if (d.success) { setHeroSaved(true); setTimeout(() => setHeroSaved(false), 3000); }
    } catch {}
    finally { setHeroSaving(false); }
  };

  const saveSiteSettings = async () => {
    try {
      const r = await fetch('/api/portal/site-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(siteSettings),
      });
      const d = await r.json();
      if (d.success) { setSettingsSaved(true); setTimeout(() => setSettingsSaved(false), 3000); }
    } catch {}
  };

  const togglePortal = async (villageId: string, currentState: boolean) => {
    try {
      await fetch(`/api/community-portals/${villageId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ published: !currentState }),
      });
      setPortals(ps => ps.map(p => p.id === villageId ? { ...p, published: !currentState } : p));
    } catch {}
  };

  const inp = "w-full bg-[#0E1812] border border-[#1C2E22] rounded-lg px-3 py-2.5 text-[#EDE8DC] text-sm focus:outline-none focus:border-[#C9A84C] transition-colors placeholder:text-[#6B8070]";
  const lbl = "text-xs font-medium text-[#B8C4BB] mb-1.5 block";

  return (
    <div className="min-h-screen bg-[#070D0A] flex" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed md:static inset-y-0 left-0 z-50 w-64 flex-shrink-0
        bg-[#0E1812] border-r border-[#1C2E22]
        flex flex-col transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        {/* Sidebar header */}
        <div className="p-5 border-b border-[#1C2E22]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0F5132] flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-[#C9A84C]" />
            </div>
            <div className="min-w-0">
              <p className="text-[#C9A84C] font-bold text-sm truncate">Super Admin</p>
              <p className="text-[#6B8070] text-xs truncate">erueuzairue@gmail.com</p>
            </div>
            <button className="md:hidden ml-auto text-[#6B8070]" onClick={() => setSidebarOpen(false)}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 overflow-y-auto space-y-1">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            return (
              <button key={item.id}
                onClick={() => { setActiveNav(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm
                  font-medium transition-all text-left
                  ${activeNav === item.id
                    ? 'bg-[#0F5132]/40 text-[#C9A84C] border border-[#0F5132]/60'
                    : 'text-[#B8C4BB] hover:text-[#EDE8DC] hover:bg-[#121E16]'}`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {item.label}
                {activeNav === item.id && <ChevronRight className="w-3 h-3 ml-auto" />}
              </button>
            );
          })}
        </nav>

        {/* Sidebar footer */}
        <div className="p-3 border-t border-[#1C2E22] space-y-2">
          <button onClick={() => onNavigate('festival-home')}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm
                       text-[#B8C4BB] hover:text-[#EDE8DC] hover:bg-[#121E16] transition-colors">
            <Globe className="w-4 h-4" /> View Public Site
          </button>
          <button onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm
                       text-red-400 hover:bg-red-900/20 transition-colors">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="bg-[#0E1812] border-b border-[#1C2E22] px-4 py-3 flex items-center gap-3 sticky top-0 z-30">
          <button onClick={() => setSidebarOpen(true)} className="md:hidden text-[#B8C4BB]">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-[#EDE8DC] font-bold text-base truncate">
              {NAV_ITEMS.find(n => n.id === activeNav)?.label || 'Dashboard'}
            </h1>
            <p className="text-[#6B8070] text-xs">ERUE Festival Suite · Admin Panel</p>
          </div>
          <div className="text-right hide-mobile">
            <p className="text-[#C9A84C] text-xs font-semibold">Grand Finale</p>
            <p className="text-[#6B8070] text-xs">30 December 2026</p>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader className="w-8 h-8 text-[#C9A84C] animate-spin" />
            </div>
          ) : (

            <>
            {/* ── DASHBOARD ─────────────────────────────────────────── */}
            {activeNav === 'dashboard' && (
              <div className="space-y-6">
                <div>
                  <div className="section-badge mb-3">Welcome back, Hyginus</div>
                  <h2 className="text-2xl font-bold text-[#EDE8DC] font-cinzel">
                    ERUE Festival 2026
                  </h2>
                  <p className="text-[#6B8070] text-sm mt-1">
                    Grand Finale: Wednesday, 30 December 2026 · Jattu-Uzairue
                  </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {stats.map((s, i) => (
                    <div key={i} className="stat-card">
                      <span className="text-2xl">{s.icon}</span>
                      <span className={`stat-value ${s.color}`}>{s.value}</span>
                      <span className="stat-label">{s.label}</span>
                    </div>
                  ))}
                </div>

                {/* Quick actions */}
                <div>
                  <h3 className="text-[#C9A84C] font-semibold text-sm mb-3 gold-accent inline-block">
                    Quick Actions
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                    {[
                      { label: 'Upload Hero Images',   nav: 'hero',          icon: '📸' },
                      { label: 'Manage Communities',   nav: 'communities',   icon: '🏘️' },
                      { label: 'Edit Festival Info',   nav: 'festival',      icon: '📅' },
                      { label: 'View Registrations',   nav: 'registrations', icon: '📋' },
                    ].map(a => (
                      <button key={a.nav} onClick={() => setActiveNav(a.nav)}
                        className="royal-card p-4 text-left hover:border-[#C9A84C]/40 transition-all group">
                        <span className="text-2xl block mb-2">{a.icon}</span>
                        <span className="text-sm font-medium text-[#B8C4BB] group-hover:text-[#EDE8DC]">
                          {a.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Community portals overview */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-[#C9A84C] font-semibold text-sm gold-accent inline-block">
                      Community Portals Status
                    </h3>
                    <button onClick={() => setActiveNav('communities')}
                      className="text-xs text-[#6B8070] hover:text-[#C9A84C] transition-colors">
                      View all →
                    </button>
                  </div>
                  <div className="royal-card overflow-hidden mt-3">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-[#1C2E22]">
                            {['Community', 'Status', 'Chief', 'Team', 'Posts'].map(h => (
                              <th key={h} className="text-left px-4 py-3 text-[#6B8070] text-xs font-medium">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {portals.slice(0, 8).map(p => (
                            <tr key={p.id} className="border-b border-[#1C2E22]/50 hover:bg-[#121E16] transition-colors">
                              <td className="px-4 py-3 text-[#EDE8DC] font-medium text-xs">{p.name || p.id}</td>
                              <td className="px-4 py-3">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                                  ${p.published ? 'bg-green-900/30 text-green-400' : 'bg-slate-800 text-slate-500'}`}>
                                  {p.published ? '🌐 Live' : '🔒 Draft'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-xs ${p.hasChief ? 'text-green-400' : 'text-[#6B8070]'}`}>
                                  {p.hasChief ? '✓' : '—'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-[#B8C4BB] text-xs">{p.team}</td>
                              <td className="px-4 py-3 text-[#B8C4BB] text-xs">{p.posts}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── HERO IMAGES ───────────────────────────────────────── */}
            {activeNav === 'hero' && (
              <div className="space-y-6 max-w-4xl">
                <div>
                  <h2 className="text-xl font-bold text-[#EDE8DC] font-cinzel mb-1">Hero Section Images</h2>
                  <p className="text-[#6B8070] text-sm">
                    Upload the 3 official images for the website hero section.
                    <strong className="text-[#C9A84C]"> No AI or stock images.</strong>
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { key: 'palaceImageUrl',   title: '🏛️ Palace of the Ogieneni',      desc: 'Exterior photograph of the royal palace in Jattu-Uzairue' },
                    { key: 'ogieneniImageUrl', title: '👑 HRH The Ogieneni',             desc: 'Official portrait of the Ogieneni of Uzairue Kingdom' },
                    { key: 'logoImageUrl',     title: '⭐ Uzairue Kingdom Official Logo', desc: 'The official crest/logo of Uzairue Kingdom' },
                  ].map(slot => (
                    <div key={slot.key} className="royal-card p-4 space-y-3">
                      <div>
                        <p className="text-[#EDE8DC] font-semibold text-sm">{slot.title}</p>
                        <p className="text-[#6B8070] text-xs mt-1">{slot.desc}</p>
                      </div>

                      {heroImages[slot.key as keyof typeof heroImages] ? (
                        <div className="space-y-2">
                          <img src={heroImages[slot.key as keyof typeof heroImages]}
                            alt={slot.title}
                            className="w-full h-40 object-cover rounded-lg border border-[#1C2E22]" />
                          <button
                            onClick={() => setHeroImages(h => ({ ...h, [slot.key]: '' }))}
                            className="text-xs text-red-400 hover:text-red-300 transition-colors">
                            × Remove image
                          </button>
                        </div>
                      ) : (
                        <div className="upload-slot h-40"
                          onClick={() => document.getElementById(`hero-upload-${slot.key}`)?.click()}>
                          <Image className="w-8 h-8 text-[#2A4A34] mb-2" />
                          <p className="text-[#6B8070] text-xs">Click to upload</p>
                          <p className="text-[#2A4A34] text-[10px] mt-1">JPG, PNG, WebP · Max 10MB</p>
                          <input id={`hero-upload-${slot.key}`} type="file" accept="image/*" className="hidden"
                            onChange={async e => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const fd = new FormData();
                              fd.append('file', file);
                              fd.append('folder', 'erue-festival/hero');
                              const r = await fetch('/api/community-portals/upload', { method: 'POST', body: fd });
                              const d = await r.json();
                              if (d.success) setHeroImages(h => ({ ...h, [slot.key]: d.url }));
                            }}
                          />
                        </div>
                      )}

                      <div>
                        <label className={lbl}>Or paste image URL</label>
                        <input value={heroImages[slot.key as keyof typeof heroImages]}
                          onChange={e => setHeroImages(h => ({ ...h, [slot.key]: e.target.value }))}
                          placeholder="https://..."
                          className={inp} />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-4">
                  <button onClick={saveHeroImages} disabled={heroSaving}
                    className="btn-gold px-6 py-2.5 text-sm disabled:opacity-50">
                    {heroSaving ? 'Saving…' : '💾 Save Hero Images'}
                  </button>
                  {heroSaved && (
                    <span className="text-green-400 text-sm flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Saved successfully
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* ── COMMUNITIES ───────────────────────────────────────── */}
            {activeNav === 'communities' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-[#EDE8DC] font-cinzel mb-1">22 Community Portals</h2>
                  <p className="text-[#6B8070] text-sm">Manage all community sub-portals of Uzairue Kingdom.</p>
                </div>
                <div className="royal-card overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#1C2E22] bg-[#0E1812]">
                          {['#', 'Community', 'Status', 'Chief', 'Team', 'Posts', 'Hero', 'Actions'].map(h => (
                            <th key={h} className="text-left px-4 py-3 text-[#6B8070] text-xs font-medium">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: 22 }, (_, i) => {
                          const vId   = `v-${i + 1}`;
                          const names = [
                            'Jattu-Uzairue','Afashio-Uzairue','Afowa-Uzairue','Apana-Uzairue',
                            'Ayaoghena-Uzairue','Ayogwiri-Uzairue','Ayua-Uzairue','Elele-Uzairue',
                            'Idatto-Uzairue','Ikabigbo-Uzairue','Ikholo-Uzairue','Imeke-Uzairue',
                            'Imhonikhe-Uzairue','Irekpai-Uzairue','Iyamho-Uzairue','Iyora-Uzairue',
                            'Iyuku-Uzairue','Ogbido-Uzairue','Ozor-Uzairue','Ugbenor-Uzairue',
                            'Uluoke-Uzairue','Yelwa-Uzairue'
                          ];
                          const portal = portals.find(p => p.id === vId);
                          return (
                            <tr key={vId} className="border-b border-[#1C2E22]/50 hover:bg-[#121E16] transition-colors">
                              <td className="px-4 py-3 text-[#6B8070] text-xs">{i+1}</td>
                              <td className="px-4 py-3">
                                <span className="text-[#EDE8DC] font-medium text-xs">{names[i]}</span>
                              </td>
                              <td className="px-4 py-3">
                                <button onClick={() => portal && togglePortal(vId, portal.published)}
                                  className={`text-xs px-2 py-0.5 rounded-full font-medium transition-colors
                                    ${portal?.published
                                      ? 'bg-green-900/30 text-green-400 hover:bg-red-900/30 hover:text-red-400'
                                      : 'bg-slate-800 text-slate-500 hover:bg-green-900/30 hover:text-green-400'}`}>
                                  {portal?.published ? '🌐 Live' : '🔒 Draft'}
                                </button>
                              </td>
                              <td className="px-4 py-3 text-xs">
                                <span className={portal?.hasChief ? 'text-green-400' : 'text-[#6B8070]'}>
                                  {portal?.hasChief ? '✓' : '—'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-[#B8C4BB] text-xs">{portal?.team || 0}</td>
                              <td className="px-4 py-3 text-[#B8C4BB] text-xs">{portal?.posts || 0}</td>
                              <td className="px-4 py-3 text-xs">
                                <span className={portal?.hasHero ? 'text-green-400' : 'text-[#6B8070]'}>
                                  {portal?.hasHero ? '✓' : '—'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <button onClick={() => onNavigate(`community-${vId}`)}
                                  className="text-xs text-[#C9A84C] hover:underline">
                                  Edit →
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── FESTIVAL SETTINGS ─────────────────────────────────── */}
            {activeNav === 'festival' && (
              <div className="space-y-6 max-w-2xl">
                <div>
                  <h2 className="text-xl font-bold text-[#EDE8DC] font-cinzel mb-1">Festival Settings</h2>
                  <p className="text-[#6B8070] text-sm">Edit site-wide festival information and details.</p>
                </div>
                <div className="royal-card p-6 space-y-4">
                  {[
                    { key: 'siteName',      label: 'Site Name',          placeholder: 'ERUE Festival Suite' },
                    { key: 'tagline',       label: 'Tagline / Motto',    placeholder: '"One People, One Kingdom, One Destiny"' },
                    { key: 'festivalStart', label: 'Festival Start Date',placeholder: '2026-12-24', type: 'date' },
                    { key: 'festivalEnd',   label: 'Grand Finale Date',  placeholder: '2026-12-30', type: 'date' },
                    { key: 'grandFinale',   label: 'Grand Finale Label', placeholder: 'Wednesday, 30 December 2026' },
                    { key: 'venueName',     label: 'Primary Venue Name', placeholder: 'Palace of the Ogieneni' },
                    { key: 'venueAddress',  label: 'Venue Address',      placeholder: 'Jattu-Uzairue, Etsako West, Edo State' },
                    { key: 'contactEmail',  label: 'Contact Email',      placeholder: 'erueuzairue@gmail.com', type: 'email' },
                    { key: 'contactPhone',  label: 'Contact Phone',      placeholder: '+234...' },
                    { key: 'facebookUrl',   label: 'Facebook Page URL',  placeholder: 'https://facebook.com/...' },
                    { key: 'whatsappNumber',label: 'WhatsApp Number',    placeholder: '+234...' },
                  ].map(field => (
                    <div key={field.key}>
                      <label className={lbl}>{field.label}</label>
                      <input
                        type={field.type || 'text'}
                        value={siteSettings[field.key as keyof typeof siteSettings]}
                        onChange={e => setSiteSettings(s => ({ ...s, [field.key]: e.target.value }))}
                        placeholder={field.placeholder}
                        className={inp}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-4">
                  <button onClick={saveSiteSettings} className="btn-gold px-6 py-2.5 text-sm">
                    💾 Save Festival Settings
                  </button>
                  {settingsSaved && (
                    <span className="text-green-400 text-sm flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Saved
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* ── CONTENT MANAGER ───────────────────────────────────── */}
            {activeNav === 'content' && (
              <div>
                {/* News stats bar */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:20 }}>
                  {[
                    { label:'Total Articles', value:newsStats.total,     color:'#C9A84C' },
                    { label:'Published',      value:newsStats.published, color:'#22c55e' },
                    { label:'Drafts',         value:newsStats.drafts,    color:'#f59e0b' },
                    { label:'🔴 Breaking',    value:newsStats.breaking,  color:'#ef4444' },
                  ].map(s=>(
                    <div key={s.label} style={{ background:'#121E16', border:'1px solid #1C2E22', borderRadius:10, padding:'12px 16px' }}>
                      <div style={{ fontSize:22, fontWeight:700, color:s.color }}>{s.value}</div>
                      <div style={{ fontSize:11, color:'rgba(237,232,220,0.5)' }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                <ErueNewsPortal currentUser={currentUser} adminMode={true} />
              </div>
            )}

            {activeNav === 'users' && (
              <UserManagementPanel currentUser={currentUser} />
            )}

            {activeNav === 'registrations' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-[#EDE8DC] font-cinzel">Registrations Manager</h2>
                <div className="royal-card p-6 text-center py-12">
                  <UserCheck className="w-12 h-12 text-[#6B8070] mx-auto mb-3" />
                  <p className="text-[#B8C4BB] font-medium">Registration Hub</p>
                  <p className="text-[#6B8070] text-sm mt-1">View and manage all festival event registrations.</p>
                  <button onClick={() => onNavigate('registrations')}
                    className="btn-royal mt-4 px-6 py-2.5 text-sm">
                    Open Registration Hub →
                  </button>
                </div>
              </div>
            )}

            {/* ── FINANCIAL ─────────────────────────────────────────── */}
            {activeNav === 'store-admin' && (
              <StoreAdminPanel currentUser={currentUser} />
            )}

            {activeNav === 'financial' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-[#EDE8DC] font-cinzel">Financial Dashboard</h2>
                <div className="royal-card p-6 text-center py-12">
                  <DollarSign className="w-12 h-12 text-[#6B8070] mx-auto mb-3" />
                  <p className="text-[#B8C4BB] font-medium">Financial Manager</p>
                  <p className="text-[#6B8070] text-sm mt-1">Revenue, expenses, and sponsorship tracking.</p>
                  <button onClick={() => onNavigate('financial-manager')}
                    className="btn-royal mt-4 px-6 py-2.5 text-sm">
                    Open Financial Manager →
                  </button>
                </div>
              </div>
            )}

            {/* ── ANALYTICS ─────────────────────────────────────────── */}
            {activeNav === 'analytics' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-[#EDE8DC] font-cinzel">Analytics</h2>
                <button onClick={() => onNavigate('admin')}
                  className="btn-royal px-6 py-2.5 text-sm">
                  Open Full Analytics Dashboard →
                </button>
              </div>
            )}

            {/* ── SYSTEM SETTINGS ───────────────────────────────────── */}
            
            {/* ── QUICK SETUP ─────────────────────────────────────── */}
            {activeNav === 'quicksetup' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-[#EDE8DC] font-cinzel">
                  Community Portal Quick Setup
                </h2>
                <CommunityQuickSetup currentUser={currentUser} />
              </div>
            )}

            {activeNav === 'system' && (
              <div className="space-y-6 max-w-xl">
                <div>
                  <h2 className="text-xl font-bold text-[#EDE8DC] font-cinzel mb-1">System Settings</h2>
                  <p className="text-[#6B8070] text-sm">Platform configuration and security settings.</p>
                </div>
                <div className="royal-card p-6 space-y-4">
                  <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg p-4">
                    <p className="text-amber-400 font-semibold text-sm">⚠️ Pre-Launch Checklist</p>
                    <ul className="text-amber-300/70 text-xs mt-2 space-y-1">
                      {[
                        'Upload palace photograph to hero section',
                        'Upload Ogieneni portrait to hero section',
                        'Upload official Uzairue Kingdom logo',
                        'Set up all 22 community portals',
                        'Rotate all default passwords',
                        'Verify DNS for erueuzairue.org',
                        'Test all forms on mobile devices',
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-amber-600 mt-0.5">□</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-[#C9A84C] font-semibold text-sm mb-3">Server Information</p>
                    <div className="space-y-2">
                      {[
                        ['VPS',        'Contabo · 31.220.80.91'],
                        ['OS',         'Ubuntu 24.04.4 LTS'],
                        ['Domain',     'erueuzairue.org'],
                        ['Staging',    'eruefestsuite.digitraxfinteca.com'],
                        ['Database',   'PostgreSQL 15 · eruefestdb'],
                        ['Container',  'eruefestsuite-container · Port 3009'],
                        ['Storage',    'Cloudinary · uuovmrek'],
                      ].map(([k, v]) => (
                        <div key={k} className="flex gap-3 text-xs">
                          <span className="text-[#6B8070] w-20 flex-shrink-0">{k}</span>
                          <span className="text-[#B8C4BB] font-mono">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
